import { useMemo } from 'react';
import Form, { Rule } from 'epn-ui/umd/dist/components/Form';
import InputText from 'epn-ui/umd/dist/components/InputText';
import Select from 'epn-ui/umd/dist/components/Select';
import DatePicker from 'epn-ui/umd/dist/components/DatePicker';
import Grid from 'epn-ui/umd/dist/components/Grid';

// Helpers
import useI18n from '@i18n';
import { isNumber } from '../../WalletsForm/utils';
import { IWalletTemplateForm } from '../interfaces';

// Config
import { regexpValidation, RENAME_ERRORS_BY_TYPE } from '../../WalletsForm/config';

export type IWmzTemplateProps = Pick<IWalletTemplateForm, 'countries' | 'countriesIsLoading'>;

const WmzTemplate: React.FC<IWmzTemplateProps> = ({ countries, countriesIsLoading }) => {
    const { t } = useI18n('Dashboard');
    const { t: tCommon } = useI18n('Common');

    const countriesWithId = useMemo(() => {
        if (!Array.isArray(countries)) return [];
        if (countries.length === 0) return [];

        return countries.map(country => ({
            ...country,
            id: `ModalAddWalletCountrySelect_option_${country.value}`,
        }));
    }, [countries]);

    return (
        <>
            <Form.Item
                name="walletName"
                label={t('Purse name')}
                rules={[
                    {
                        validator: async (_rule: Rule, value: string | null | undefined) => {
                            if (isNumber(value)) {
                                throw new Error(t(RENAME_ERRORS_BY_TYPE.notNumber));
                            }
                        },
                        validateTrigger: 'onBlur',
                    },
                ]}
            >
                <InputText
                    id="ModalAddWalletPurseNameInput"
                    maxLength={50}
                    placeholder={t('Enter purse name')}
                />
            </Form.Item>

            <Form.Item
                label={t('Purse number')}
                name="walletNumber"
                rules={[
                    {
                        required: true,
                        message: tCommon('Required field'),
                        validateTrigger: 'onBlur',
                    },
                    {
                        pattern: regexpValidation.wmz,
                        message: t('Purse number should start with Z and 12 digits'),
                        validateTrigger: 'onBlur',
                    },
                ]}
            >
                <InputText id="ModalAddWalletPurseNumberInput" mask="{Z}000000000000" />
            </Form.Item>

            <Grid wrap gutter={[16, 0]}>
                <Grid.Col span={12}>
                    <Form.Item
                        label={tCommon('Name')}
                        name="name"
                        rules={[
                            {
                                required: true,
                                message: tCommon('Required field'),
                                validateTrigger: 'onBlur',
                            },
                            {
                                pattern: regexpValidation.latin,
                                message: t('Only latin symbols'),
                            },
                        ]}
                    >
                        <InputText
                            id="ModalAddWalletNameInput"
                            placeholder={tCommon('Enter name')}
                        />
                    </Form.Item>
                </Grid.Col>

                <Grid.Col span={12}>
                    <Form.Item
                        label={tCommon('Surname')}
                        name="surname"
                        rules={[
                            {
                                required: true,
                                message: tCommon('Required field'),
                                validateTrigger: 'onBlur',
                            },
                            {
                                pattern: regexpValidation.latin,
                                message: t('Only latin symbols'),
                            },
                        ]}
                    >
                        <InputText
                            id="ModalAddWalletSurnameInput"
                            placeholder={tCommon('Enter surname')}
                        />
                    </Form.Item>
                </Grid.Col>

                <Grid.Col span={12}>
                    <Form.Item
                        label={t('Date of birth')}
                        name="dateOfBirth"
                        rules={[{ required: true, message: tCommon('Required field') }]}
                    >
                        <DatePicker
                            id="ModalAddWalletDateOfBirthInput"
                            className="w-full"
                            placeholder="31.12.2000"
                            format="DD.MM.YYYY"
                        />
                    </Form.Item>
                </Grid.Col>

                <Grid.Col span={12}>
                    <Form.Item
                        name="country"
                        label={tCommon('Country')}
                        rules={[
                            {
                                required: true,
                                message: tCommon('Required field'),
                                validateTrigger: 'onBlur',
                            },
                        ]}
                    >
                        <Select
                            id="ModalAddWalletCountrySelect"
                            loading={countriesIsLoading}
                            disabled={countriesIsLoading}
                            placeholder={tCommon('Choose country')}
                            options={countriesWithId}
                        />
                    </Form.Item>
                </Grid.Col>
            </Grid>
        </>
    );
};

export default WmzTemplate;
